import { Component } from '@theme/component';

/**
 * B2B VIP Enquiry drawer (product page).
 *
 * Lets the Contact Form submit natively (full page request) rather than
 * intercepting it with fetch — Shopify's Contact Form endpoint requires an
 * hCaptcha token that's only attached during a real browser submission, so
 * an AJAX POST is rejected outright with "Missing CAPTCHA token". Before
 * that native submit fires, the product, variant and desired quantity are
 * packaged into the hidden contact[body] field so the merchant's B2B team
 * receives exactly what was requested by email — Shopify has no native
 * automatic quoting/pricing, so a human still replies manually.
 *
 * Shopify redirects back to this same page with `?contact_posted=true`
 * and the form's id as the URL hash on success; connectedCallback checks
 * for that on load and re-opens the drawer straight into the success
 * state so the confirmation isn't lost to the page reload.
 *
 * @typedef {object} Refs
 * @property {HTMLFormElement} form
 * @property {HTMLElement} formState
 * @property {HTMLElement} successState
 * @property {HTMLInputElement} quantityInput
 * @property {HTMLInputElement} bodyField
 *
 * @extends {Component<Refs>}
 */
export class SphatikB2bEnquiryComponent extends Component {
  requiredRefs = ['form', 'formState', 'successState', 'quantityInput', 'bodyField'];

  connectedCallback() {
    super.connectedCallback();
    this.#syncBody();
    this.#restoreSuccessIfJustPosted();
  }

  handleQuantityInput() {
    this.#syncBody();
  }

  /** Keeps the hidden body field current right before the native submit proceeds. */
  handleSubmit() {
    this.#syncBody();
  }

  #restoreSuccessIfJustPosted() {
    const url = new URL(window.location.href);
    if (url.searchParams.get('contact_posted') !== 'true') return;
    if (decodeURIComponent(url.hash.slice(1)) !== this.refs.form.id) return;

    this.#showSuccess();
    this.closest('theme-drawer')?.open();
  }

  #syncBody() {
    const { quantityInput, bodyField } = this.refs;
    const quantity = quantityInput.value ? `${quantityInput.value} pcs` : 'Not specified';
    const lines = [
      'Wholesale quote request',
      `Product: ${this.dataset.productTitle}`,
    ];

    if (this.dataset.variantTitle) lines.push(`Variant: ${this.dataset.variantTitle}`);
    lines.push(`Requested quantity: ${quantity}`);

    bodyField.value = lines.join('\n');
  }

  #showSuccess() {
    const { formState, successState } = this.refs;

    formState.setAttribute('hidden', '');
    successState.removeAttribute('hidden');

    // rAF so the icon's transitions play from their initial state rather than skipping straight to it.
    requestAnimationFrame(() => {
      this.classList.add('sphatik-b2b-enquiry--success');
    });
  }
}

if (!customElements.get('sphatik-b2b-enquiry-component')) {
  customElements.define('sphatik-b2b-enquiry-component', SphatikB2bEnquiryComponent);
}
